import React from 'react';
import { Truck, Crown, Cake, CheckCircle2, Lock, Gift } from 'lucide-react';

/**
 * Minimal premium reward milestones row.
 *
 * Renders the configured tiers (read from `/api/customers/me/rewards`
 * → `config.reward_milestones`) as a clean horizontal scroll.
 *
 * Three visual states per tile:
 *   - unlocked  → gold border + soft gold glow + CheckCircle, dimmed CTA text
 *   - active    → gold border, animated gold progress bar, "N pts to <name>"
 *   - locked    → muted gray border + Lock icon, 40% opacity
 *
 * No casino / arcade. Inspired by Stripe / Linear / Apple tier UIs.
 */

const ICONS = { truck: Truck, crown: Crown, cake: Cake, gift: Gift };

const computeStates = (milestones, points) => {
  // Sort ascending by points threshold to make `active` deterministic
  const sorted = [...(milestones || [])].sort((a, b) => (a.points || 0) - (b.points || 0));
  let activeAssigned = false;
  return sorted.map((m) => {
    const threshold = Number(m.points || 0);
    const unlocked = points >= threshold;
    let state = 'locked';
    if (unlocked) state = 'unlocked';
    else if (!activeAssigned) { state = 'active'; activeAssigned = true; }
    const progress = threshold > 0 ? Math.min(1, points / threshold) : 0;
    const toGo = Math.max(0, threshold - points);
    return { ...m, threshold, state, progress, toGo };
  });
};

const RewardMilestonesRow = ({ milestones = [], points = 0 }) => {
  const tiles = computeStates(milestones, Number(points || 0));
  if (!tiles.length) return null;

  // Headline copy adapts to state of the next reward
  const next = tiles.find((t) => t.state === 'active');
  let eyebrow = 'Reward milestones';
  let lead = 'Earn points on every order. Unlock perks as you go.';
  if (next) {
    eyebrow = 'Next reward approaching';
    lead = `${next.toGo.toLocaleString()} points until ${next.name}`;
  } else if (tiles.every((t) => t.state === 'unlocked')) {
    eyebrow = 'All milestones unlocked';
    lead = 'You\u2019ve unlocked every reward. Stay tuned for new tiers.';
  }

  return (
    <section className="mt-6" data-testid="reward-milestones">
      <div className="flex items-end justify-between mb-3">
        <div>
          <p className="text-[10.5px] uppercase tracking-[0.22em] text-gold-400 font-bold">
            {eyebrow}
          </p>
          <h3 className="mt-1 text-[16px] sm:text-[17px] font-bold text-white">
            {lead}
          </h3>
        </div>
        <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-white/40">
          <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-gold-400" /> active</span>
          <span className="inline-flex items-center gap-1.5 ml-3"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> unlocked</span>
        </div>
      </div>

      <div
        className="-mx-1 px-1 flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="reward-milestones-row"
      >
        {tiles.map((t) => (
          <MilestoneTile key={t.id || t.name} tile={t} />
        ))}
      </div>
    </section>
  );
};

const MilestoneTile = ({ tile }) => {
  const Icon = ICONS[tile.icon] || Gift;
  const { state, name, threshold, toGo, progress } = tile;

  const isUnlocked = state === 'unlocked';
  const isActive = state === 'active';

  return (
    <div
      data-testid={`milestone-tile-${tile.id || name.toLowerCase().replace(/\s+/g, '-')}`}
      data-state={state}
      className={[
        'snap-start relative shrink-0 w-[230px] sm:w-[260px] rounded-2xl border p-4 transition-all',
        isUnlocked
          ? 'border-gold-500/55 bg-gradient-to-br from-gold-500/[0.08] via-zinc-950 to-black shadow-[0_18px_40px_-22px_rgba(212,175,55,0.55)]'
          : isActive
          ? 'border-gold-500/35 bg-gradient-to-br from-zinc-950 to-black hover:border-gold-500/55'
          : 'border-white/8 bg-white/[0.02] opacity-60 hover:opacity-80',
      ].join(' ')}
    >
      {/* Soft halo for unlocked only */}
      {isUnlocked ? (
        <div
          aria-hidden="true"
          className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-gold-500/15 blur-2xl pointer-events-none"
        />
      ) : null}

      <div className="relative flex items-start justify-between mb-3">
        <div
          className={[
            'w-10 h-10 rounded-xl border flex items-center justify-center transition-colors',
            isUnlocked
              ? 'bg-gradient-to-b from-amber-300 to-gold-500 border-gold-500/0'
              : isActive
              ? 'bg-gold-500/10 border-gold-500/30'
              : 'bg-white/[0.04] border-white/10',
          ].join(' ')}
        >
          <Icon
            className={isUnlocked ? 'w-4.5 h-4.5 text-black' : 'w-4.5 h-4.5 text-gold-400'}
            strokeWidth={isUnlocked ? 2.25 : 1.85}
          />
        </div>

        {isUnlocked ? (
          <span
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold uppercase tracking-wider"
            data-testid={`milestone-status-${tile.id || name}`}
          >
            <CheckCircle2 className="w-3 h-3" /> Unlocked
          </span>
        ) : isActive ? (
          <span
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gold-500/10 border border-gold-500/30 text-gold-300 text-[10px] font-bold uppercase tracking-wider"
            data-testid={`milestone-status-${tile.id || name}`}
          >
            Active
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/[0.04] border border-white/10 text-white/45 text-[10px] font-bold uppercase tracking-wider"
            data-testid={`milestone-status-${tile.id || name}`}
          >
            <Lock className="w-3 h-3" /> Locked
          </span>
        )}
      </div>

      <div className="relative">
        <div className={`text-[15px] font-bold leading-tight ${isUnlocked ? 'text-white' : isActive ? 'text-white' : 'text-white/70'}`}>
          {name}
        </div>
        <div className={`text-[12px] mt-0.5 ${isUnlocked ? 'text-gold-300' : 'text-white/45'}`}>
          {threshold.toLocaleString()} pts
        </div>
      </div>

      {/* Progress bar (always rendered; full when unlocked, fractional when active, empty when locked) */}
      <div className="relative mt-4">
        <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
          <div
            className={[
              'h-full transition-[width] duration-700 ease-out',
              isUnlocked
                ? 'bg-gradient-to-r from-emerald-400 to-emerald-500'
                : isActive
                ? 'bg-gradient-to-r from-amber-300 to-gold-500'
                : 'bg-white/15',
            ].join(' ')}
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>
        <div className="mt-2 text-[11px]">
          {isUnlocked ? (
            <span className="text-emerald-300/85 font-semibold">You unlocked {name}</span>
          ) : isActive ? (
            <span className="text-white/55">
              <span className="text-gold-300 font-semibold">{toGo.toLocaleString()}</span> pts to go
            </span>
          ) : (
            <span className="text-white/35">Locked — keep earning to unlock</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default RewardMilestonesRow;
